/* Imports needed for all question form subclasses. */
import * as React from 'react';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import * as _ from 'lodash';
import {
    mapStateToQuestionFormBaseProps,
    mapDispatchToQuestionFormBaseProps
} from '../../QuestionFormBase';

import {
    QuestionFormWithOptionsBase,
    QuestionFormWithOptionsBaseProps,
    QuestionFormWithOptionsBaseStates
} from '../QuestionFormWithOptionsBase';
import NotifyHOC from '../../../../../../HOC/Notifications';
import {
    QTYPES,
    Question,
    IPopupCard,
    QuestionID,
    QuestionSummary,
    MULTIMEDIA_TYPES,
    VIEW_MODE,
    OPTION_SIZE,
    PROJECT_STATUS
} from '@nexxt/common/types';
import * as uuid from 'uuid';
/* Components used in this question form. */
import BuilderHeader from '../../../common/builderHeader';
import { PopupCard } from '../../../common/popupCard';
import QuestionSettings from '../../../common/questionSettings';
import PipeOptionModal from '../../../common/pipeOptionModal';
import OptionPackModal from '../../../../../libraries/optionpack/OptionPackModal';
import { OPTION_TYPES } from '@nexxt/common/types';
import {
    parseQuestionSummaryToObjects,
    resetKey
} from '../../../../../../utils/question';
import { createNewRichTextObject } from '@nexxt/common/services/TextService';
import Options from '../../../common/Options';
import MultimediaBubble from '../../../common/MultimediaBubble';
import { UilTimes } from '@iconscout/react-unicons';

export type Props = QuestionFormWithOptionsBaseProps;

export interface States extends QuestionFormWithOptionsBaseStates {
    // nothing special... yet!
    currentTab: number;
}

const MAX_OPTIONS = 20;

class FormPopup extends QuestionFormWithOptionsBase<Props, States> {
    constructor(p: QuestionFormWithOptionsBaseProps) {
        super(p, QTYPES.POPUP);
        let selectedCard = 0,
            question: Question = this.state.question;
        if (this.props.currentQuestionId) {
            selectedCard =
                this.state.question.childrenIds.indexOf(
                    this.props.currentQuestionId
                ) || 0;
        } else {
            question = this.getDefaultPopup(this.state.question);
        }

        // backward compatibility
        const popupCardTypes = (question?.popupSettings?.popupCardTypes ||
            question.selectedOptionType) as OPTION_TYPES;

        question = {
            ...question,
            min: this.props.isNewQuestion ? 1 : question.min,
            max: this.props.isNewQuestion ? 1 : question.max,
            popupSettings: {
                ...question.popupSettings,
                popupCardTypes
            }
        };
        this.state = {
            ...this.state,
            currentTab: selectedCard !== -1 ? selectedCard : 0,
            question
        };
    }

    componentDidUpdate(prevProps, prevState) {
        super.componentDidUpdate(prevProps, prevState);
        if (prevState.currentTab > this.state.question.popupCards.length) {
            this.setState({
                currentTab: this.state.question.popupCards.length
            });
        }
    }
    componentWillReceiveProps(nextProps: Props) {
        if (
            nextProps.questionSummaries !== this.props.questionSummaries &&
            !this.props.isNewQuestion
        ) {
            // Find this popup question using props.questionSummaries
            const question = parseQuestionSummaryToObjects(
                nextProps.questionSummaries
            )[this.state.question.id];
            this.getSortedPopupCard(question);
        }
    }

    /**
     * Get default popup question data
     */
    getDefaultPopup = (question): Question => {
        return {
            ...question,
            title: '',
            randomGroup: true,
            popupCards: [
                {
                    type: QTYPES.POPUP_CARD,
                    title: '',
                    questionTexts: [],
                    mediaURL: ''
                }
            ],
            popupSettings: {
                popupCardTypes: OPTION_TYPES.TEXT_ONLY
            },
            setupQuestion: {
                type: QTYPES.MULTIMEDIA,
                projectID: question.projectID,
                id: 0,
                continueTexts: [],
                title: this.getUniqueQuestionLabel(),
                parent: 0,
                childrenIds: [],
                showNotApplicableOption: false,
                showContinueButton: false,
                questionTexts: [],
                altTexts: [],
                notApplicableTexts: [],
                showFollowUp: false,
                followUpTexts: [],
                allowUserEdit: true,
                isRichText: false,
                selectedAlignOption: 'center',
                imageDisplay: 'square',
                selectedOptionType: 'imageOnly',
                random: false,
                optionsLayout: 1,
                pipingQuestions: [],
                piping: false,
                embeddedURL: '',
                playVideo: false,
                showImage: false,
                showText: true
            }
        };
    };
    /**
     * To support the previous questionTexts,
     */
    parsePopupCards = (question) => {
        const parsedCards = question.popupCards.map((c) => {
            const newQuestionTexts = c.questionTexts.map((text) => {
                if (!text.richTexts) {
                    return {
                        language: text.language,
                        richTexts: [text.text]
                    };
                }
                return text;
            });

            return {
                ...c,
                questionTexts: newQuestionTexts
            };
        });
        return { ...question, popupCards: parsedCards };
    };

    /**
     * Re-arrange the position of popup cards
     */
    refreshPopupCards = (questionIDsInOrder: QuestionID[]) => {
        const popupCards = this.state.question.popupCards;
        const newPopup = [];
        questionIDsInOrder.forEach((questionID, index) => {
            newPopup.push({
                ...popupCards.find((card) => card.id === questionID),
                position: index + 1
            });
        });
        return newPopup;
    };

    // Check if the popup cards positions get changed.
    // Compare the position to the value in props.questionSummaries
    getSortedPopupCard = (questionSummary: QuestionSummary) => {
        const popupCardIDsFromQuestionSummary =
            questionSummary?.childrenSummaries
                .map(
                    (child) =>
                        [QTYPES.POPUP_CARD, QTYPES.MULTI_POPUP_CARD].includes(
                            child.type
                        ) && child.id
                )
                .filter((x) => x) || [];

        const cardIDsFromQuestion =
            this.state.question.popupCards
                .map((card) => card.id)
                .filter((x) => x) || [];
        if (_.isEqual(popupCardIDsFromQuestionSummary, cardIDsFromQuestion)) {
            return;
        }
        // If a user tried to sort the popup question and there is more than 0 new cards
        else if (
            !_.isEqual(popupCardIDsFromQuestionSummary, cardIDsFromQuestion) &&
            popupCardIDsFromQuestionSummary.length <= cardIDsFromQuestion.length
        ) {
            const newCards = this.state.question.popupCards.filter(
                (card) => !card.id
            );
            this.setState({
                question: {
                    ...this.state.question,
                    popupCards: [
                        ...this.refreshPopupCards(
                            popupCardIDsFromQuestionSummary
                        ),
                        ...newCards
                    ]
                }
            }),
                this.setEditFlag;
        }
    };

    /**
     * set setup question value
     */
    setSetupQuestion = (keyName: string, value: any) => {
        this.setState(
            {
                question: {
                    ...this.state.question,
                    setupQuestion: {
                        ...this.state.question.setupQuestion,
                        [keyName]: value
                    } as Question
                }
            },
            this.setEditFlag
        );
    };

    /**
     * Set setup question questionText value
     */
    setSetupQuestionWithLanguage = (
        keyName: string,
        value: any,
        language: string,
        index: number
    ) => {
        const question = this.state.question.setupQuestion;
        const currentObject = question[keyName] || [];
        let uploadedValue = false;
        const newTextField = currentObject.filter(
            (c) => c.language === language
        )[0] || {
            language,
            richTexts: []
        };
        newTextField.richTexts[index] = value;
        const currentLangauge = currentObject.map((c) => {
            if (c.language === language) {
                uploadedValue = true;
                return { ...c, richTexts: newTextField.richTexts };
            }
            return c;
        });

        if (!uploadedValue) {
            this.setState(
                {
                    question: {
                        ...this.state.question,
                        setupQuestion: {
                            ...this.state.question.setupQuestion,
                            [keyName]: [...currentLangauge, newTextField]
                        }
                    }
                },
                this.setEditFlag
            );
            return;
        }
        this.setState(
            {
                question: {
                    ...(this.state.question as Question),
                    setupQuestion: {
                        ...this.state.question.setupQuestion,
                        [keyName]: [...currentLangauge]
                    }
                }
            },
            this.setEditFlag
        );
    };

    NEW_setSetupQuestionWithLanguage = (
        keyName: string,
        value: any,
        language: string,
        index: number,
        type = MULTIMEDIA_TYPES.RICH_TEXT
    ) => {
        const question = this.state.question.setupQuestion;
        const currentObject = question[keyName] || [];
        let uploadedValue = false;
        let newFields = currentObject.filter(
            (current) => current.language === language
        )[0];
        switch (type) {
            case MULTIMEDIA_TYPES.RICH_TEXT:
                newFields = newFields || {
                    language,
                    contents: [{ type, richText: {}, id: uuid.v4() }]
                };
                newFields.contents = [
                    ...newFields.contents.slice(0, index),
                    { ...newFields.contents[index], type, richText: value },
                    ...newFields.contents.slice(index + 1)
                ];
                break;
            case MULTIMEDIA_TYPES.IMAGE:
            case MULTIMEDIA_TYPES.AUDIO:
            case MULTIMEDIA_TYPES.VIDEO:
                newFields.contents = [
                    ...newFields.contents.slice(0, index),
                    { ...newFields.contents[index], type, ...value },
                    ...newFields.contents.slice(index + 1)
                ];
                break;
        }

        const currentLanguage = currentObject.map((current) => {
            if (current.language === language) {
                uploadedValue = true;

                return { ...current, contents: newFields.contents };
            }
            return current;
        });

        if (!uploadedValue) {
            currentObject.push(newFields);
            this.setState(
                {
                    question: {
                        ...this.state.question,
                        setupQuestion: {
                            ...this.state.question.setupQuestion,
                            [keyName]: currentObject
                        }
                    }
                },
                this.setEditFlag
            );
            return;
        }
        this.setState(
            {
                question: {
                    ...(this.state.question as Question),
                    setupQuestion: {
                        ...this.state.question.setupQuestion,
                        [keyName]: [...currentLanguage]
                    }
                }
            },
            this.setEditFlag
        );
    };

    NEW_addNewEditor = (
        languageCode: string,
        position: number,
        type = MULTIMEDIA_TYPES.RICH_TEXT,
        data?: any
    ) => {
        const questionTexts = this.state.question.setupQuestion.questionTexts;
        const languageIndex = questionTexts.findIndex(
            (questionText) => questionText.language === languageCode
        );
        let newContent;
        if (languageIndex > -1) {
            switch (type) {
                case MULTIMEDIA_TYPES.RICH_TEXT:
                    newContent = {
                        id: uuid.v4(),
                        type,
                        richText: createNewRichTextObject('')
                    };

                    break;
                case MULTIMEDIA_TYPES.IMAGE:
                case MULTIMEDIA_TYPES.VIDEO:
                case MULTIMEDIA_TYPES.AUDIO:
                    newContent = {
                        id: uuid.v4(),
                        type,
                        mediaURL: data?.mediaURL,
                        mediaOptions: { size: OPTION_SIZE.MEDIUM },
                        answerReference: data?.answerReference
                    };
            }

            if (newContent) {
                questionTexts[languageIndex].contents = [
                    ...questionTexts[languageIndex].contents.slice(0, position),
                    newContent,
                    ...questionTexts[languageIndex].contents.slice(position)
                ];
            }
            this.setState(
                {
                    question: {
                        ...this.state.question,
                        setupQuestion: {
                            ...this.state.question.setupQuestion,
                            questionTexts
                        }
                    }
                },
                this.setEditFlag
            );
        }
    };

    NEW_removeEditor = (languageCode: string, index: number) => {
        const questionTexts = this.state.question.setupQuestion.questionTexts;
        const languageIndex = questionTexts.findIndex(
            (questionText) => questionText.language === languageCode
        );

        if (languageIndex > -1) {
            questionTexts[languageIndex].contents.splice(index, 1);

            this.setState(
                {
                    question: {
                        ...this.state.question,
                        setupQuestion: {
                            ...this.state.question.setupQuestion,
                            questionTexts
                        }
                    }
                },
                this.setEditFlag
            );
        }
    };
    updatePopupType = (popupCardTypes: OPTION_TYPES) => {
        this.setState(
            {
                ...this.state,
                question: {
                    ...this.state.question,
                    popupSettings: {
                        ...this.state.question.popupSettings,
                        popupCardTypes
                    }
                }
            },
            this.setEditFlag
        );
    };

    /**
     * Update pop up cards
     */

    updatePopupCard = (data, deletedOptionIDs = []) => {
        this.setState(
            {
                question: {
                    ...this.state.question,
                    popupCards: [...data]
                } as Question
            },
            this.setEditFlag
        );
    };

    updatePopupCardType = (
        type: QTYPES.POPUP_CARD | QTYPES.MULTI_POPUP_CARD
    ) => {
        this.setState(
            (prevState) => ({
                question: {
                    ...prevState.question,
                    popupCards: prevState.question.popupCards.map((card) => ({
                        ...card,
                        type
                    }))
                } as Question
            }),
            this.setEditFlag
        );
    };
    /**
     * Add new popup card.
     */
    addNewPopupCard = () => {
        const popupCards: IPopupCard[] = [
            ...(this.state.question.popupCards || [])
        ];

        popupCards.push({
            type: popupCards[popupCards.length - 1].type,
            title: popupCards[popupCards.length - 1].title,
            questionTexts: resetKey(
                popupCards[popupCards.length - 1].questionTexts
            ),
            mediaURL: ''
        });
        this.setState(
            {
                question: {
                    ...this.state.question,
                    popupCards: popupCards
                },
                currentTab: popupCards.length
            },
            this.setEditFlag
        );
    };

    /**
     * @Disabled Delete popup card button.
     * Decided to move all actions to the buttons on question list.
     */
    handleRemoveCardField = (index) => {
        let deleteId;
        if (this.state.question.popupCards[index].id) {
            deleteId = this.state.question.popupCards[index].id;
        }
        const deletedCards: number[] = this.state.question.deleteCardIds || [];
        if (index >= 0) {
            const optionFields = [...(this.state.question.popupCards || [])];
            const cardError: any =
                this.getValidationService().checkPopupcardWithLanguage(
                    this.state.question,
                    'popupCards',
                    this.props.currentProject.languages,
                    this.props.questionTitles
                );
            optionFields.splice(index, 1);

            this.setState(
                (prevState) => ({
                    question: {
                        ...prevState.question,
                        popupCards: optionFields,
                        deleteCardIds: deleteId
                            ? deletedCards.concat(deleteId)
                            : deletedCards
                    },
                    questionError: {
                        ...prevState.questionError,
                        popupCards: cardError.errors
                    },
                    currentTab: 0
                }),
                this.setEditFlag
            );
        }
    };
    /**
     * Renders all option cards based on option type: Text only, Image only, Image and Text
     */
    renderPopupCard = (index) => {
        const questionError =
            (this.state.showError && this.state.questionError.popupCards) || {};
        const selectedOptionType =
            this.state.question.popupSettings.popupCardTypes;
        return (
            <PopupCard
                openIndex={index}
                selectedOptionType={selectedOptionType}
                setQuestion={this.setQuestion}
                question={this.state.question}
                cards={this.state.question.popupCards}
                currentCard={
                    this.state.question.popupCards[this.state.currentTab - 1]
                }
                setValueWithLanguage={this.setQuestionWithLanguage}
                questionError={questionError}
                updatePopupCard={this.updatePopupCard}
                editEnabled={this.props.editEnabled}
            />
        );
    };

    render() {
        const { currentProject } = this.props;
        const {
            question,
            isSettingBoxLayoutOpen,
            isPipeOptionModalOpen,
            isOptionPackModalOpen,
            currentTab
        } = this.state;
        const questionError =
            (this.state.showError && this.state.questionError) || {};
        const setupError =
            questionError.setupQuestion &&
            (questionError.setupQuestion.title !== '' ||
                questionError.setupQuestion.questionTexts.isError);
        return (
            <div>
                <div className="project-add--question-builder">
                    {isPipeOptionModalOpen && (
                        <PipeOptionModal
                            isOpen={isPipeOptionModalOpen}
                            onClose={this.togglePipeOptionModal}
                            showRandomize={this.state.question.random}
                            setSelectedPipeOption={this.setQuestion}
                            value={this.state.question.pipingQuestions}
                        />
                    )}
                    {isOptionPackModalOpen && (
                        <OptionPackModal
                            isOpen={isOptionPackModalOpen}
                            onCancel={this.toggleOptionPackModal}
                            onDone={this.onSelectOpenPack}
                        />
                    )}
                    <BuilderHeader
                        toggleSettingBoxLayout={this.toggleSettingBoxLayout}
                        isSettingBoxLayoutOpen={isSettingBoxLayoutOpen}
                        showAlert={!this.props.editEnabled}
                        alertMessages={this.getWarningMessage()}
                        question={question.setupQuestion}
                        setValue={this.setSetupQuestion}
                        errorObject={this.state.questionError?.setupQuestion}
                    />
                    <div className="builder-body">
                        {(isSettingBoxLayoutOpen && (
                            <QuestionSettings
                                onClose={this.toggleSettingBoxLayout}
                                question={question}
                                questionSettings={{
                                    allowUserEdit: true,
                                    randomGroup: true,
                                    popupCardType: true
                                }}
                                setValue={this.setQuestion}
                                disabled={!this.props.editEnabled}
                            />
                        )) ||
                            null}
                        <div className="builder-tap">
                            <div className="builder-tap--wrap">
                                <li
                                    className={`builder-tap--item ${
                                        currentTab === 0 ? ' active' : ''
                                    } ${setupError ? 'error' : ''}`}
                                    onClick={(e) =>
                                        this.setState({ currentTab: 0 })
                                    }
                                >
                                    <a>Question setup</a>
                                </li>
                                {question.popupCards.map((card, index) => {
                                    const title: string = card.title || '';
                                    const error =
                                        (this.state.showError &&
                                            this.state.questionError
                                                .popupCards) ||
                                        {};
                                    const errorCard =
                                        error[index] &&
                                        (error[index].title !== '' ||
                                            error[index].mediaURL !== '' ||
                                            error[index].questionTexts.isError);
                                    return (
                                        <div
                                            key={index}
                                            style={{ position: 'relative' }}
                                        >
                                            <li
                                                className={`builder-tap--item ${
                                                    currentTab === index + 1
                                                        ? ' active'
                                                        : ''
                                                } ${errorCard ? 'error' : ''}`}
                                                onClick={(e) =>
                                                    this.setState({
                                                        currentTab: index + 1
                                                    })
                                                }
                                            >
                                                <a>
                                                    {title
                                                        ? title.substr(0, 10)
                                                        : `Cards (${
                                                              index + 1
                                                          })`}
                                                    &nbsp;
                                                </a>
                                            </li>
                                        </div>
                                    );
                                })}
                            </div>
                            {this.props.editEnabled && (
                                <div className="builder-tap--options-wrapper">
                                    <button
                                        className="card-add"
                                        disabled={!this.props.editEnabled}
                                        onClick={this.addNewPopupCard}
                                    >
                                        <i className="nx nx-add" />
                                    </button>
                                </div>
                            )}
                        </div>
                        {currentTab === 0 ? (
                            <React.Fragment>
                                {this.renderErrorNotes()}
                                <MultimediaBubble
                                    question={question.setupQuestion}
                                    languages={currentProject.languages}
                                    setValue={this.setSetupQuestion}
                                    setValueWithLanguage={
                                        this.NEW_setSetupQuestionWithLanguage
                                    }
                                    addNewBubble={this.NEW_addNewEditor}
                                    removeBubble={this.NEW_removeEditor}
                                    disabled={!this.props.editEnabled}
                                    questionError={questionError.setupQuestion}
                                    settings={{ showClose: true }}
                                />
                                {/* {this.renderOptionAddButton(
                                        (this.state.question.options || [])
                                            .length >= MAX_OPTIONS
                                    )}
                                    {this.state.question.options.length >=
                                        MAX_OPTIONS && (
                                        <div
                                            className="form-group error"
                                            style={{ margin: 0 }}
                                        >
                                            <label className="form-group__label form-group__label--block">
                                                You&apos;ve hit the maximum
                                                number of options. Please remove
                                                an option to add more.
                                            </label>
                                        </div>
                                    )} */}
                                <div>
                                    <Options
                                        question={question}
                                        setQuestionAsQuestion={
                                            this.setQuestionAsQuestion
                                        }
                                        setQuestion={this.setQuestion}
                                        setQuestionWithLanguage={
                                            this.setQuestionWithLanguage
                                        }
                                        questionSummaries={
                                            this.props.questionSummaries
                                        }
                                        setMultimediaText={
                                            this.NEW_setQuestionTextTranslation
                                        }
                                        setOtherOptionWithLanguage={
                                            this.setOtherOptionWithLanguage
                                        }
                                        handleOptions={this.handleOptions}
                                        updatePopupCardType={
                                            this.updatePopupCardType
                                        }
                                        languages={currentProject.languages}
                                        countries={currentProject.countries}
                                        settings={{
                                            hideMultiple: true,
                                            showMultiplePopup:
                                                this.props.viewMode ===
                                                VIEW_MODE.DEV,
                                            maxOptionNum: MAX_OPTIONS
                                        }}
                                        disabled={!this.props.editEnabled}
                                        showError={this.state.showError}
                                        projectHealthOptionIds={this.getProjectHealthOptionIds()}
                                        isLive={
                                            currentProject.status ==
                                            PROJECT_STATUS.LIVE
                                        }
                                        previousPackageQuestion={
                                            (currentProject.previousPackage &&
                                                currentProject.previousPackage.questions.find(
                                                    (q) => q.id == question.id
                                                )) ||
                                            null
                                        }
                                    />
                                </div>
                                {question.showContinueButton && (
                                    <MultimediaBubble
                                        languages={currentProject.languages}
                                        question={question}
                                        setValue={this.setQuestion}
                                        setValueWithLanguage={
                                            this.NEW_setQuestionTextTranslation
                                        }
                                        questionError={questionError}
                                        keyName="continueTexts"
                                        disabled={!this.props.editEnabled}
                                        disableAddBubble
                                        defaultTextType={MULTIMEDIA_TYPES.TEXT}
                                        questionText="Continue button text"
                                        defaultText="BUTTON.CONTINUE"
                                        settings={{
                                            noBorder: true,
                                            useTranslation: true
                                        }}
                                    />
                                )}
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                {this.renderErrorNotes()}
                                <div className="option-settings-block">
                                    {this.renderPopupCard(currentTab)}{' '}
                                    {question.popupCards.length > 1 && (
                                        <button
                                            className="btn btn--md btn--orange mb-4"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                this.handleRemoveCardField(
                                                    currentTab - 1
                                                );
                                            }}
                                        >
                                            Delete Card
                                        </button>
                                    )}
                                </div>
                            </React.Fragment>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

export default withRouter(
    NotifyHOC(
        connect(
            mapStateToQuestionFormBaseProps,
            mapDispatchToQuestionFormBaseProps
        )(FormPopup)
    )
);
